import {bootstrapRuntimeState, collectRuntimeDiagnostics, detectStandaloneMode} from './appRuntime.js'
import {updateRuntimeDebugState} from './debugState.js'
import {installAppFetchPatch} from './fetchPatch.js'
import {syncPushSubscription} from './pushNotifications.js'
import {logRuntimeEvent, markFormSubmitStarted} from './telemetry.js'
import {registerAppServiceWorker} from './serviceWorker.js'
import {isChunkLoadFailure, recoverFromFrontendFailure} from './AppErrorBoundary.jsx'

let observersInstalled = false
let preloadObserverInstalled = false

function describeTarget(target) {
  if (!(target instanceof Element)) return null

  const text = typeof target.textContent === 'string'
    ? target.textContent.trim().slice(0, 80)
    : ''

  return {
    tagName: target.tagName.toLowerCase(),
    id: target.id || null,
    name: target.getAttribute('name') || null,
    type: target.getAttribute('type') || null,
    text: text || null
  }
}

function installSubmitObservers() {
  if (observersInstalled || typeof document === 'undefined') return

  document.addEventListener('submit', event => {
    const target = describeTarget(event.target)
    markFormSubmitStarted({
      source: 'form',
      target,
    })
    logRuntimeEvent('submit_clicked', {
      source: 'form',
      target
    }, {flush: false})
  }, true)

  document.addEventListener('click', event => {
    const target = event.target instanceof Element
      ? event.target.closest('button,[type="submit"],[data-submit-action]')
      : null

    if (!target) return

    if (!target.closest('form') && target.hasAttribute('data-submit-action')) {
      markFormSubmitStarted({
        source: 'button',
        target: describeTarget(target),
      })
    }

    logRuntimeEvent('submit_clicked', {
      source: 'click',
      target: describeTarget(target)
    }, {flush: false})
  }, true)

  observersInstalled = true
}

function installPreloadErrorObserver() {
  if (preloadObserverInstalled || typeof window === 'undefined') return

  window.addEventListener('vite:preloadError', event => {
    const error = event?.payload || event?.reason || event?.error || event
    if (!isChunkLoadFailure(error)) return

    if (typeof event.preventDefault === 'function') {
      event.preventDefault()
    }

    logRuntimeEvent('frontend_chunk_load_failed', {
      source: 'vite_preload_error',
      message: typeof error?.message === 'string'
        ? error.message.slice(0, 240)
        : 'Vite preload failed',
    })
    void recoverFromFrontendFailure('frontend-preload-error')
  })

  preloadObserverInstalled = true
}

export async function bootstrapApplicationRuntime() {
  logRuntimeEvent('auth_rehydration_started', {
    source: 'app_bootstrap',
  }, {flush: false})
  const diagnostics = bootstrapRuntimeState()
  updateRuntimeDebugState({
    appBootTimestamp: new Date().toISOString(),
    authHydrated: true,
    authRehydrationInFlight: false,
    authRehydrationFinishedAt: new Date().toISOString(),
  })
  logRuntimeEvent('auth_rehydration_finished', {
    source: 'app_bootstrap',
    isAuthenticated: diagnostics.auth?.isAuthenticated === true,
  }, {flush: false})
  installAppFetchPatch()
  installSubmitObservers()
  installPreloadErrorObserver()

  logRuntimeEvent('app_boot', diagnostics)
  if (detectStandaloneMode()) {
    logRuntimeEvent('standalone_detected', diagnostics)
  }

  void registerAppServiceWorker().then(() => syncPushSubscription({requestPermission: false})).catch(() => null)

  return collectRuntimeDiagnostics()
}
